import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";

const splashBg = "/assets/splash-bg.jpg";
const DURATION = 4000;

export function SplashScreen({ onFinish }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onFinish, 1000);
    }, DURATION);
    return () => clearTimeout(timer);
  }, [onFinish]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="splash"
          data-testid="splash-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="fixed inset-0 z-50"
          style={{ background: "#000", overflow: "hidden" }}
        >
          {/* Background image with slow zoom */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2, ease: "easeOut" }}
            style={{
              backgroundImage: `url(${splashBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
            }}
          />

          {/* Pulsing bright flash overlay */}
          <motion.div
            className="absolute inset-0"
            animate={{
              opacity: [0, 0.15, 0, 0.08, 0, 0.2, 0, 0],
            }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(255,160,40,0.4), transparent 70%)",
            }}
          />

          {/* Quick white flash at start */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            style={{ background: "rgba(255,255,255,0.3)" }}
          />

          {/* Flickering vignette */}
          <motion.div
            className="absolute inset-0"
            animate={{
              opacity: [0.6, 0.9, 0.5, 0.85, 0.6],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background: "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 20%, rgba(0,0,0,0.7) 100%)",
            }}
          />

          {/* Horizontal light sweep */}
          <motion.div
            className="absolute inset-0"
            animate={{ x: ["-100%", "200%"] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
            style={{
              width: "30%",
              background: "linear-gradient(90deg, transparent, rgba(255,200,80,0.08), rgba(255,255,255,0.06), transparent)",
              filter: "blur(40px)",
            }}
          />

          {/* Subtle scanline flicker */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{ opacity: [0.03, 0.08, 0.03] }}
            transition={{ duration: 0.15, repeat: Infinity }}
            style={{
              backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.05) 2px, rgba(255,255,255,0.05) 4px)",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
